import { createContext, Dispatch, ReactNode, useReducer, useMemo, useRef } from "react";
import { blankState, AppState, AppAction, reducer, ActionType } from "./State";
import { compareObjects, getWorldCoordsToCenterHex } from "./stateUtils";

// NOTHING TO CHANGE BELOW THIS LINE
const getLocalState = () => {
  const localStateSource = localStorage.getItem("state");

  const localStateJSON = localStateSource ? JSON.parse(localStateSource) : null;
  return localStateJSON?.hash === blankState.hash ? localStateSource : null;
};

const localState = getLocalState();
let initialState: AppState = localState ? JSON.parse(localState) : blankState;
// Ensure new state fields exist when loading from localStorage (e.g. after deploy with new schema)
if (initialState.tileZIndexes === undefined) initialState.tileZIndexes = {};
if (initialState.invertMapPan === undefined) initialState.invertMapPan = false;
if (initialState.highQuality === undefined) initialState.highQuality = false;
if (initialState.hexGridBuffer === undefined) initialState.hexGridBuffer = 0;
if (initialState.hexTileCache === undefined) initialState.hexTileCache = {};
else initialState.hexTileCache = {}; // blob URLs don't survive reload

const isNFTAlerWasClosedLocalStorage = localStorage.getItem(
  "isNFTAlerWasClosedLocalStorage"
);

if (isNFTAlerWasClosedLocalStorage) {
  initialState.isNFTAlertPopupOpen = false;
}

initialState.isLoginPopupOpen = false;
initialState.isPlayerProfilePopupOpen = false;
initialState.generatingNowHexs = [];
initialState.isHexInfoPopupOpen = false;
initialState.selectedHexInfo = null;
initialState.regeneratedHexagons = [];
initialState.generatedHexagons = [];
const path = (typeof window !== "undefined" ? window.location.pathname : "").replace(/\/$/, "") || "";
const hexRouteMatch = path.match(/^\/hex\/(-?\d+)\/(-?\d+)(\/view)?$/);

if (hexRouteMatch) {
  const x = parseInt(hexRouteMatch[1], 10);
  const y = parseInt(hexRouteMatch[2], 10);
  initialState = reducer(initialState, { type: ActionType.SELECT_HEX, x, y });
  // Do not open popup here: open only after requestHexagenInfo confirms hex exists (see App.tsx)
  const coords = getWorldCoordsToCenterHex(x, y, initialState.worldScale);
  initialState.worldX = coords.worldX;
  initialState.worldY = coords.worldY;
}

// Check for player URL
const playerRegex = /^\/player\/([^/]+)/;
const playerMatch = path.match(playerRegex);

if (playerMatch) {
  const uid = playerMatch[1];
  initialState = reducer(initialState, { type: ActionType.SELECT_PLAYER, uid });
  initialState.isPlayerInfoPopupOpen = true;
}

// Direct link /search: open map with search popup
if (path === "/search") {
  initialState.isSearchPopupOpen = true;
}

export const AppContext = createContext<{
  state: AppState;
  dispatch: Dispatch<AppAction>;
}>({
  state: initialState,
  dispatch: () => null,
});

/** State for hexes: same as AppContext but stable when only worldX/worldY change (field moves, hex positions don't). */
export const HexStateContext = createContext<{
  state: AppState;
  dispatch: Dispatch<AppAction>;
}>({
  state: initialState,
  dispatch: () => null,
});

const loggedReducer = (state: AppState, action: AppAction) => {
  const updatedState = reducer(state, action);
  action.logggable &&
    console.log(
      `\nACTION:\n${JSON.stringify(
        action,
        null,
        2
      )}\n\nCHANGES:\n${compareObjects(state, updatedState)}\n `
    );
  return updatedState;
};

interface AppProviderProps {
  children: ReactNode;
}
const appValue = (state: AppState, dispatch: Dispatch<AppAction>) => ({ state, dispatch });

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(loggedReducer, initialState);
  const prevForHexRef = useRef(state);
  const hexState = useMemo(() => {
    const prev = prevForHexRef.current;
    const positionChanged = state.worldX !== prev.worldX || state.worldY !== prev.worldY;
    if (positionChanged) {
      let onlyPosition = true;
      for (const k of Object.keys(state) as (keyof AppState)[]) {
        if (k === "worldX" || k === "worldY") continue;
        if ((state as AppState)[k] !== (prev as AppState)[k]) {
          onlyPosition = false;
          break;
        }
      }
      if (onlyPosition) return prev;
    }
    prevForHexRef.current = state;
    return state;
  }, [state]);
  const hexValue = useMemo(() => ({ state: hexState, dispatch }), [hexState, dispatch]);

  globalDispatch = dispatch;
  globalState = state;

  localStorage.setItem("state", JSON.stringify(state));

  return (
    <AppContext.Provider value={appValue(state, dispatch)}>
      <HexStateContext.Provider value={hexValue}>
        {children}
      </HexStateContext.Provider>
    </AppContext.Provider>
  );
};

let globalDispatch: Dispatch<AppAction> = () => null;
let globalState: AppState = initialState;

export const getGlobalDispatch = () => {
  return globalDispatch;
};

export const getGlobalState = () => {
  return globalState;
};
